<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const mobileOpen = ref(false)

const regularNav = [
  ['/', '工作台', '⌂', 'user-overview'],
  ['/dicom', 'DICOM 批处理', '◫', 'user-dicom'],
  ['/pdf', 'PDF 脱敏', '▤', 'user-pdf'],
  ['/image-convert', '影像转 DICOM', '◎', 'user-image-convert'],
  ['/account', '账户设置', '○', 'account'],
]
const adminNav = [
  ['/admin', '管理总览', '⌂', 'admin-overview'],
  ['/admin/tasks', '业务任务', '▤', 'admin-tasks'],
  ['/admin/dicom', 'DICOM 规则', '◇', 'admin-dicom'],
  ['/admin/pdf', 'PDF 规则', '▧', 'admin-pdf'],
  ['/admin/users', '用户管理', '♙', 'admin-users'],
  ['/admin/logs', '操作与访问日志', '≡', 'admin-logs'],
  ['/account', '账户设置', '○', 'account'],
]
const navigation = computed(() => (auth.isAdmin ? adminNav : regularNav))
const activeNavKey = computed(() => {
  if (auth.isAdmin && /^\/(dicom|pdf)\/batches\//.test(route.path)) return 'admin-tasks'
  return route.meta.navKey
})

async function logout() {
  await auth.logout()
  await router.replace('/login')
}
</script>

<template>
  <div class="shell">
    <button class="mobile-menu" type="button" aria-label="打开菜单" @click="mobileOpen = !mobileOpen">☰</button>
    <aside class="sidebar" :class="{ open: mobileOpen }">
      <div>
        <div class="brand">
          <span class="brand-mark">V</span>
          <div><strong>视界</strong><small>医疗数据治理</small></div>
        </div>
        <nav aria-label="主导航">
          <RouterLink
            v-for="[path, label, icon, navKey] in navigation"
            :key="path"
            :to="path"
            :class="{ active: activeNavKey === navKey }"
            @click="mobileOpen = false"
          >
            <span aria-hidden="true">{{ icon }}</span>{{ label }}
          </RouterLink>
        </nav>
      </div>
      <div class="profile">
        <div class="avatar">{{ auth.user?.username?.slice(0, 1).toUpperCase() }}</div>
        <div class="profile-copy">
          <strong>{{ auth.user?.username }}</strong>
          <span>{{ auth.user?.real_name }}</span>
        </div>
        <button type="button" title="退出登录" aria-label="退出登录" @click="logout">↗</button>
      </div>
    </aside>
    <main class="content"><slot /></main>
    <button v-if="mobileOpen" class="backdrop" aria-label="关闭菜单" @click="mobileOpen = false" />
  </div>
</template>

<style scoped>
.shell { min-height: 100vh; }
.sidebar { position: fixed; inset: 0 auto 0 0; z-index: 20; width: 270px; display: flex; flex-direction: column; justify-content: space-between; padding: 28px 20px 20px; background: var(--navy-950); color: #d8e6ef; }
.brand { display: flex; gap: 12px; align-items: center; padding: 0 10px 28px; }
.brand-mark { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 14px; background: var(--teal-500); color: white; font-size: 22px; font-weight: 900; }
.brand strong, .brand small { display: block; }
.brand strong { color: white; font-size: 19px; letter-spacing: .08em; }
.brand small { margin-top: 3px; color: #8ca8ba; font-size: 11px; letter-spacing: .12em; }
nav { display: grid; gap: 5px; }
nav a { display: flex; align-items: center; gap: 12px; min-height: 44px; padding: 0 13px; border-radius: 11px; color: #a9c0ce; font-size: 14px; font-weight: 650; }
nav a:hover { background: rgb(255 255 255 / 6%); color: white; }
nav a.active { background: linear-gradient(90deg, rgb(11 155 144 / 27%), rgb(11 155 144 / 8%)); color: white; box-shadow: inset 3px 0 var(--teal-500); }
nav a span { width: 22px; color: #67c9c0; text-align: center; font-size: 18px; }
.profile { display: flex; align-items: center; gap: 10px; padding: 15px 10px 2px; border-top: 1px solid rgb(255 255 255 / 10%); }
.avatar { display: grid; place-items: center; width: 38px; height: 38px; flex: none; border-radius: 50%; background: var(--teal-700); color: white; font-weight: 800; }
.profile-copy { min-width: 0; flex: 1; }
.profile-copy strong, .profile-copy span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.profile-copy strong { color: white; font-size: 13px; }
.profile-copy span { margin-top: 3px; color: #8ca8ba; font-size: 11px; }
.profile button { border: 0; background: transparent; color: #8ca8ba; font-size: 18px; }
.content { min-height: 100vh; margin-left: 270px; }
.mobile-menu, .backdrop { display: none; }
@media (max-width: 900px) {
  .content { margin-left: 0; padding-top: 58px; }
  .sidebar { transform: translateX(-105%); transition: transform .2s; box-shadow: var(--shadow-md); }
  .sidebar.open { transform: translateX(0); }
  .mobile-menu { position: fixed; top: 12px; left: 14px; z-index: 19; display: grid; place-items: center; width: 42px; height: 42px; border: 1px solid var(--slate-200); border-radius: 11px; background: white; color: var(--navy-900); }
  .backdrop { position: fixed; inset: 0; z-index: 18; display: block; border: 0; background: rgb(7 27 46 / 42%); }
}
</style>
